import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Tooltip,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  QrCode as QrCodeIcon,
  Logout as LogoutIcon,
  RestartAlt as RestartIcon,
  Edit as EditIcon,
  WhatsApp as WhatsAppIcon,
  Sms as SmsIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import api from '../../utils/api'; // Заменяем axios на api
import DashboardLayout from '../../components/DashboardLayout';

const WAAccounts = () => {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openQrDialog, setOpenQrDialog] = useState(false);
  const [openEditLimitDialog, setOpenEditLimitDialog] = useState(false);
  const [openTagsDialog, setOpenTagsDialog] = useState(false);
  const [openChatDialog, setOpenChatDialog] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [newInstanceAlias, setNewInstanceAlias] = useState('');
  const [newInstanceType, setNewInstanceType] = useState('whatsapp'); // 'whatsapp' | 'max'
  const [qrCode, setQrCode] = useState('');
  const [refreshingStatus, setRefreshingStatus] = useState({});
  const [qrCodeTimer, setQrCodeTimer] = useState(20);
  const [qrCodeExpired, setQrCodeExpired] = useState(false);
  const [newMessageLimit, setNewMessageLimit] = useState(200);
  const [tagsInput, setTagsInput] = useState('');
  const [tagsFilter, setTagsFilter] = useState('');
  const [chatPhone, setChatPhone] = useState('');
  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const [aliasInput, setAliasInput] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });


  // Fetch instances on a component mount
  useEffect(() => {
    fetchInstances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check instance status without showing notification
  const checkInstanceStatus = async (instanceId) => {
    try {
      // Принудительно обновляем статус из Green API
      const response = await api.get(`/instances/${instanceId}/status`, { params: { refresh: true } });

      // Update instance in the list
      setInstances(instances.map(instance =>
          instance.id === instanceId
              ? {
                ...instance,
                status: response.data.status,
                last_activity: response.data.lastActivity,
                phone: response.data.phone // Update phone number if available
              }
              : instance
      ));

      return response.data.status;
    } catch (err) {
      console.error('Error checking instance status:', err);
      return null;
    }
  };

  // QR code timer effect
  useEffect(() => {
    let timerId;
    let statusCheckId;
    let refreshTimerId;

    // Start timer when QR code is displayed
    if (qrCode && openQrDialog && !qrCodeExpired) {
      setQrCodeTimer(20); // Reset timer to 20 seconds

      timerId = setInterval(() => {
        setQrCodeTimer(prevTimer => {
          if (prevTimer <= 1) {
            // Timer reached 0, clear interval and set QR code as expired
            clearInterval(timerId);
            setQrCodeExpired(true);

            // Check instance status when the timer expires
            if (selectedInstance) {
              statusCheckId = setTimeout(async () => {
                const status = await checkInstanceStatus(selectedInstance.id);
                const instanceType = String(selectedInstance.type || '').toLowerCase();
                const isMax = instanceType === 'max';
                
                // If the instance is connected, close the dialog
                if (status === 'connected' || status === 'authenticated' || status === 'authorized') {
                  setOpenQrDialog(false);
                  setQrCodeTimer(20);
                  setQrCodeExpired(false);
                  showSnackbar(`${isMax ? 'MAX' : 'WhatsApp'} instance connected successfully`, 'success');
                } else if (isMax) {
                  // For MAX, refresh QR code automatically when expired
                  setQrCodeExpired(false);
                  setQrCodeTimer(20);
                  getInstanceQR(selectedInstance.id);
                }
              }, 1000); // Wait 1 second before checking status
            }

            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);

      // For MAX instances, also check status periodically and refresh QR if needed
      if (selectedInstance && String(selectedInstance.type || '').toLowerCase() === 'max') {
        refreshTimerId = setInterval(async () => {
          const status = await checkInstanceStatus(selectedInstance.id);
          if (status === 'connected' || status === 'authenticated' || status === 'authorized') {
            setOpenQrDialog(false);
            setQrCodeTimer(20);
            setQrCodeExpired(false);
            showSnackbar('MAX instance connected successfully', 'success');
          }
        }, 3000); // Check every 3 seconds
      }
    }

    // Cleanup timer when component unmounts or QR code changes
    return () => {
      if (timerId) {
        clearInterval(timerId);
      }
      if (statusCheckId) {
        clearTimeout(statusCheckId);
      }
      if (refreshTimerId) {
        clearInterval(refreshTimerId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCode, openQrDialog, qrCodeExpired, selectedInstance]);

  // Block timer effect - update the UI every second to show accurate countdown
  useEffect(() => {
    // Only set up the timer if there are instances and at least one is blocked
    const hasBlockedInstances = instances.some(instance => isInstanceBlocked(instance));

    if (instances.length > 0 && hasBlockedInstances) {
      const timerId = setInterval(() => {
        // Force a re-render to update the countdown timers
        setInstances(prevInstances => [...prevInstances]);
      }, 1000);

      return () => {
        clearInterval(timerId);
      };
    }
  }, [instances]);

  // Fetch all instances for the current user with retry mechanism
  const fetchInstances = async (retryCount = 0, maxRetries = 3) => {
    setLoading(true);
    try {
      console.log('Fetching instances from API...');

      const response = await api.get('/instances?include_stats=true');

      console.log('Instances response:', response.data);
      setInstances(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching instances:', err);

      // Check if we should retry
      if (retryCount < maxRetries) {
        console.log(`Retrying... Attempt ${retryCount + 1} of ${maxRetries}`);
        // Wait for a short time before retrying (exponential backoff)
        const delay = Math.pow(2, retryCount) * 1000;
        setTimeout(() => {
          fetchInstances(retryCount + 1, maxRetries);
        }, delay);
        return;
      }

      // Handle specific error types
      if (err.response) {
        console.error('Response error data:', err.response.data);
        console.error('Response error status:', err.response.status);

        if (err.response.status === 401) {
          setError('Authentication failed. Please log in again.');
        } else if (err.response.status === 500) {
          setError('Server error. Please try again later.');
        } else {
          setError(`Failed to load WhatsApp instances: ${err.response.data.message || 'Unknown error'}`);
        }
      } else if (err.request) {
        console.error('Request error:', err.request);
        setError('No response from server. Please check your internet connection.');
      } else {
        console.error('Error message:', err.message);
        setError(`Failed to load WhatsApp instances: ${err.message}`);
      }
    } finally {
      if (retryCount === 0 || retryCount >= maxRetries) {
        setLoading(false);
      }
    }
  };

  // Create a new instance
  const createInstance = async () => {
    try {
      const response = await api.post('/instances', {
        alias: newInstanceAlias,
        type: newInstanceType
      });

      // Extract the instance object from the response to ensure it has an 'id' property
      if (response.data && response.data.instance) {
        setInstances([...instances, response.data.instance]);
      } else {
        console.error('Invalid response format:', response.data);
        showSnackbar('Received invalid data format from server', 'error');
      }

      setOpenAddDialog(false);
      setNewInstanceAlias('');
      setNewInstanceType('whatsapp');

      showSnackbar('Instance created successfully', 'success');
    } catch (err) {
      console.error('Error creating instance:', err);

      // Handle specific error types
      if (err.response) {
        if (err.response.status === 401) {
          showSnackbar('Authentication failed. Please log in again.', 'error');
        } else {
          showSnackbar(`Failed to create instance: ${err.response.data.message || 'Unknown error'}`, 'error');
        }
      } else if (err.request) {
        showSnackbar('No response from server. Please check your internet connection.', 'error');
      } else {
        showSnackbar(`Failed to create instance: ${err.message}`, 'error');
      }
    }
  };

  // Delete an instance
  const deleteInstance = async () => {
    if (!selectedInstance) return;

    try {
      await api.delete(`/instances/${selectedInstance.id}`);

      setInstances(instances.filter(instance => instance.id !== selectedInstance.id));
      setOpenDeleteDialog(false);
      setSelectedInstance(null);

      showSnackbar('Instance deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting instance:', err);
      showSnackbar(`Failed to delete instance: ${err.response?.data?.message || 'Unknown error'}`, 'error');
    }
  };

  // Update message limit for instance
  const updateMessageLimit = async () => {
    if (!selectedInstance) return;

    try {
      await api.put(`/instances/${selectedInstance.id}/message-limit`, {
        messageLimit: newMessageLimit
      });

      // Update the instance in the list
      setInstances(instances.map(instance =>
          instance.id === selectedInstance.id
              ? { ...instance, message_limit: newMessageLimit }
              : instance
      ));

      setOpenEditLimitDialog(false);
      setSelectedInstance(null);
      setNewMessageLimit(200);

      showSnackbar('Message limit updated successfully', 'success');
    } catch (err) {
      console.error('Error updating message limit:', err);
      showSnackbar(`Failed to update message limit: ${err.response?.data?.message || 'Unknown error'}`, 'error');
    }
  };

  // Get instance status
  const refreshInstanceStatus = async (instanceId) => {
    setRefreshingStatus(prev => ({ ...prev, [instanceId]: true }));

    try {
      // Принудительное обновление: бэкенд пойдёт в Green API и подтянет phone/status
      const response = await api.get(`/instances/${instanceId}/status`, { params: { refresh: true } });

      setInstances(instances.map(instance =>
          instance.id === instanceId
              ? {
                ...instance,
                status: response.data.status,
                last_activity: response.data.lastActivity,
                phone: response.data.phone, // Update phone number if available
                chats: response.data.chats ?? instance.chats,
                spam: response.data.spam ?? instance.spam
              }
              : instance
      ));

      showSnackbar('Status refreshed', 'info');
    } catch (err) {
      console.error('Error refreshing status:', err);
      showSnackbar(`Failed to refresh status: ${err.response?.data?.message || 'Unknown error'}`, 'error');
    } finally {
      setRefreshingStatus(prev => ({ ...prev, [instanceId]: false }));
    }
  };

  // Get QR code for instance
  const getInstanceQR = async (instanceId) => {
    setQrCode('');
    setOpenQrDialog(true);
    // Reset QR code timer and expired state
    setQrCodeTimer(20);
    setQrCodeExpired(false);

    try {
      // First check if the instance is already connected
      const status = await checkInstanceStatus(instanceId);
      if (status === 'connected' || status === 'authenticated' || status === 'authorized') {
        showSnackbar('Instance is already connected. QR code not available.', 'info');
        setOpenQrDialog(false);
        return;
      }

      const response = await api.get(`/instances/${instanceId}/qr`);

      if (response.data && response.data.qrCode) {
        setQrCode(response.data.qrCode);
      } else {
        // Check status again, it might have changed while we were getting the QR code
        const currentStatus = await checkInstanceStatus(instanceId);
        if (currentStatus === 'connected' || currentStatus === 'authenticated' || currentStatus === 'authorized') {
          showSnackbar('Instance is now connected. QR code not available.', 'success');
          setOpenQrDialog(false);
        } else {
          showSnackbar('QR code not available. Please try again later.', 'info');
          // Keep the dialog open but don't set the QR code
        }
      }
    } catch (err) {
      console.error('Error getting QR code:', err);

      // Handle specific error types
      if (err.response) {
        if (err.response.status === 401) {
          showSnackbar('Authentication failed. Please log in again.', 'error');
        } else if (err.response.status === 404 && err.response.data.message === 'QR code not available') {
          // This is an expected error when the instance is already connected
          // Check the current status to provide better feedback
          const currentStatus = await checkInstanceStatus(instanceId);
          if (currentStatus === 'connected' || currentStatus === 'authenticated' || currentStatus === 'authorized') {
            showSnackbar('Instance is already connected. QR code not available.', 'info');
            setOpenQrDialog(false);
          } else {
            showSnackbar('QR code not available. Please try again later.', 'info');
          }
        } else {
          showSnackbar(`Failed to get QR code: ${err.response.data.message || 'Unknown error'}`, 'error');
        }
      } else if (err.request) {
        showSnackbar('No response from server. Please check your internet connection.', 'error');
      } else {
        showSnackbar(`Failed to get QR code: ${err.message}`, 'error');
      }
      // Don't close the dialog on error
    }
  };

  // Logout from instance
  const logoutInstance = async (instanceId) => {
    try {
      await api.get(`/instances/${instanceId}/logout`);

      // Refresh instance status
      refreshInstanceStatus(instanceId);

      showSnackbar('Logged out successfully', 'success');
    } catch (err) {
      console.error('Error logging out:', err);
      showSnackbar(`Failed to logout: ${err.response?.data?.message || 'Unknown error'}`, 'error');
    }
  };

  // Restart instance
  const restartInstance = async (instanceId) => {
    try {
      await api.get(`/instances/${instanceId}/reboot`);

      // Refresh instance status
      refreshInstanceStatus(instanceId);

      showSnackbar('Instance restarted', 'success');
    } catch (err) {
      console.error('Error restarting instance:', err);
      showSnackbar(`Failed to restart instance: ${err.response?.data?.message || 'Unknown error'}`, 'error');
    }
  };

  // Show snackbar notification
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handle edit limit dialog
  const handleEditLimit = (instance) => {
    setSelectedInstance(instance);
    setNewMessageLimit(instance.message_limit || 200);
    setOpenEditLimitDialog(true);
  };

  // DataGrid columns
  const columns = [
    {
      field: 'alias',
      headerName: 'Name',
      width: 250,
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" noWrap sx={{ maxWidth: 230 }}>{params.value}</Typography>
          <Tooltip title="Rename">
            <IconButton size="small" onClick={() => { setSelectedInstance(params.row); setAliasInput(params.row.alias || ''); setOpenRenameDialog(true); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const status = params.value;
        const instanceType = String(params.row.type || '').toLowerCase();
        const isMax = instanceType === 'max';
        let color = 'default';
        let label = String(status || 'unknown');

        if (['blocked', 'deleted'].includes(status)) {
          color = 'error';
          label = 'blocked';
        } else if (['connected', 'authenticated', 'authorized'].includes(status)) {
          // Use purple for MAX, green for WhatsApp
          if (isMax) {
            color = 'default';
            label = 'authorized';
          } else {
            color = 'success';
            label = 'authorized';
          }
        } else {
          color = 'default';
          label = (status || 'unknown');
        }

        return (
          <Chip 
            size="small" 
            label={label} 
            color={color} 
            sx={{ 
              textTransform: 'none',
              ...(isMax && ['connected', 'authenticated', 'authorized'].includes(status) && {
                backgroundColor: '#9c4dcc',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#7b1fa2'
                }
              })
            }} 
          />
        );
      }
    },
    {
      field: 'phone',
      headerName: 'Phone Number',
      width: 160,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const phone = params.value;
        const instanceType = String(params.row.type || '').toLowerCase();
        const isMax = instanceType === 'max';
        const waUrl = phone ? `https://wa.me/${String(phone).replace(/\D/g, '')}` : null;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
            <Typography variant="body2">{phone || 'Not connected'}</Typography>
            {phone && !isMax && (
              <Tooltip title="Open chat">
                <IconButton size="small" onClick={() => { setChatPhone(phone); setOpenChatDialog(true); }}>
                  <WhatsAppIcon fontSize="small" color="success" />
                </IconButton>
              </Tooltip>
            )}
            {phone && isMax && (
              <Tooltip title="MAX account">
                <IconButton size="small" disabled>
                  <SmsIcon fontSize="small" sx={{ color: '#9c4dcc' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 280,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const tags = (params.value || '')
          .split(',')
          .map(t => t.trim())
          .filter(Boolean);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            {tags.slice(0, 3).map((tag, idx) => (
              <Box key={idx} sx={{
                fontSize: '0.75rem',
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                bgcolor: 'primary.light',
                color: 'primary.contrastText'
              }}>{tag}</Box>
            ))}
            {tags.length > 3 && (
              <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>+{tags.length - 3}</Box>
            )}
            <Tooltip title="Edit tags">
              <IconButton size="small" onClick={() => {
                setSelectedInstance(params.row);
                setTagsInput(params.row.tags || '');
                setOpenTagsDialog(true);
              }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    },
    {
      field: 'spam_chats',
      headerName: 'Spam/Chats',
      width: 110,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => ({ spam: params.row.spam || 0, chats: params.row.chats || 0 }),
      renderCell: (params) => {
        const spam = params.value?.spam || 0;
        const chats = params.value?.chats || 0;
        return (
          <Typography variant="body2">{`${spam}/${chats}`}</Typography>
        );
      }
    },
    {
      field: 'message_limit',
      headerName: 'Limit/Day',
      width: 80,
      headerAlign: 'center',
      renderCell: (params) => {
        const blocked = isInstanceBlocked(params.row);
        const blockTooltip = blocked ? 'This instance is blocked for the first 3 minutes after creation' : '';

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                {params.value || 200}
              </Typography>
              <Tooltip title={blocked ? blockTooltip : 'Edit message limit'}>
              <span>
                <IconButton
                    size="small"
                    onClick={() => {
                      if (blocked) {
                        showSnackbar('Instance is blocked for the first 3 minutes after creation', 'warning');
                        return;
                      }
                      handleEditLimit(params.row);
                    }}
                    disabled={blocked}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
              </Tooltip>
            </Box>
        );
      }
    },
    {
      field: 'messages_sent_24h',
      headerName: 'Sent (24h)',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const sentCount = params.value || 0;
        const limit = params.row.message_limit || 200;
        const percentage = limit > 0 ? (sentCount / limit) * 100 : 0;

        // Определяем цвет в зависимости от процента использования
        let color = 'success.main';
        if (percentage >= 90) {
          color = 'error.main';
        } else if (percentage >= 70) {
          color = 'warning.main';
        }

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color, fontWeight: 'bold' }}>
                {sentCount}
              </Typography>
            </Box>
        );
      }
    },
    {
      field: 'lifetime',
      headerName: 'Lifetime',
      width: 80,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => {
        if (!params.row.created_at) return null;

        const createdAt = new Date(params.row.created_at);
        const now = new Date();
        const diffMs = now - createdAt;

        // Calculate days, hours, minutes
        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

        // Calculate remaining block time (if blocked)
        const blocked = isInstanceBlocked(params.row);
        const blockPeriodMs = 3 * 60 * 1000; // 3 minutes in milliseconds
        const remainingBlockTimeMs = blocked ? blockPeriodMs - diffMs : 0;
        const remainingBlockTimeSeconds = Math.ceil(remainingBlockTimeMs / 1000);

        return {
          days,
          hours,
          minutes,
          seconds,
          blocked,
          remainingBlockTimeSeconds
        };
      },
      renderCell: (params) => {
        if (!params.value) return 'N/A';

        const { days, hours, minutes, blocked, remainingBlockTimeSeconds } = params.value;

        let lifetimeText;
        if (days > 0) {
          lifetimeText = `${days}d ${hours}h`;
        } else if (hours > 0) {
          lifetimeText = `${hours}h ${minutes}m`;
        } else {
          lifetimeText = `${minutes}m`;
        }

        if (blocked) {
          const remainingMinutes = Math.floor(remainingBlockTimeSeconds / 60);
          const remainingSeconds = remainingBlockTimeSeconds % 60;

          return (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2">{lifetimeText}</Typography>
                <Typography
                    variant="caption"
                    color="warning.main"
                    sx={{ fontWeight: 'bold' }}
                >
                  Blocked: {remainingMinutes}m {remainingSeconds}s left
                </Typography>
              </Box>
          );
        }

        return lifetimeText;
      }
    },
    {
      field: 'account_lifetime',
      headerName: 'Connected',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => {
        const connectedAtRaw = params.row.connected_at || params.row.connectedAt || params.row.connected || null;
        if (!connectedAtRaw) return null;
        const connectedAt = new Date(connectedAtRaw);
        if (isNaN(connectedAt.getTime())) return null;
        const now = new Date();
        const diffMs = now - connectedAt;
        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        return { days, hours, minutes };
      },
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        const { days, hours, minutes } = params.value;
        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
      }
    },
    {
      field: 'last_activity',
      headerName: 'Updated',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => params.value ? new Date(params.value) : null,
      valueFormatter: (params) => params.value ? params.value.toLocaleDateString() : 'Never'
    },
    {
      field: 'warming_enabled',
      headerName: 'Warm',
      width: 60,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => {
        const checked = Boolean(params.row.warming_enabled);
        const id = params.row.id;
        const blocked = isInstanceBlocked(params.row);
        const handleToggle = async (e) => {
          const next = e.target.checked;
          // Optimistic UI update
          setInstances(prev => prev.map(inst => inst.id === id ? { ...inst, warming_enabled: next } : inst));
          try {
            const res = await api.put(`/instances/${id}`, { warming_enabled: next });
            const updated = res.data?.instance;
            // Ensure state reflects server value if provided
            if (updated && typeof updated.warming_enabled === 'boolean') {
              setInstances(prev => prev.map(inst => inst.id === id ? { ...inst, warming_enabled: updated.warming_enabled } : inst));
            }
            showSnackbar(next ? 'Warming enabled' : 'Warming disabled', 'success');
          } catch (err) {
            console.error('Error updating warming flag:', err);
            // Revert on error
            setInstances(prev => prev.map(inst => inst.id === id ? { ...inst, warming_enabled: !next } : inst));
            showSnackbar(`Failed to update Warm: ${err.response?.data?.message || 'Unknown error'}`, 'error');
          }
        };
        return (
          <Tooltip title={blocked ? 'Recently created: some actions may be limited' : 'Toggle participating in warming'}>
            <span>
              <Switch
                size="small"
                checked={checked}
                onChange={handleToggle}
                color="primary"
              />
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const blocked = isInstanceBlocked(params.row);
        const blockTooltip = blocked ? 'This instance is blocked for the first 3 minutes after creation' : '';

        return (
            <Box>
              <Tooltip title="Refresh Status">
                <IconButton
                    onClick={() => refreshInstanceStatus(params.row.id)}
                    disabled={refreshingStatus[params.row.id]}
                >
                  {refreshingStatus[params.row.id] ? (
                      <CircularProgress size={24} />
                  ) : (
                      <RefreshIcon />
                  )}
                </IconButton>
              </Tooltip>

              <Tooltip title={
                blocked ? blockTooltip :
                    params.row.status === 'notAuthorized' || params.row.status === 'disconnected' ? 'QR Code' :
                        'QR Code (Only available for not authorized instances)'
              }>
                <span>
                  <IconButton
                      onClick={async () => {
                        setSelectedInstance(params.row);

                        if (isInstanceBlocked(params.row)) {
                          showSnackbar('Instance is blocked for the first 3 minutes after creation', 'warning');
                          return;
                        }

                        // Double-check the current status before showing QR code
                        const currentStatus = await checkInstanceStatus(params.row.id);
                        if (currentStatus === 'connected' || currentStatus === 'authenticated' || currentStatus === 'authorized') {
                          showSnackbar('Instance is already connected. QR code not available.', 'info');
                        } else if (currentStatus === 'notAuthorized' || currentStatus === 'disconnected') {
                          getInstanceQR(params.row.id);
                        } else {
                          showSnackbar(`Cannot get QR code for instance with status: ${currentStatus}. Please try again later.`, 'warning');
                        }
                      }}
                      disabled={(params.row.status !== 'notAuthorized' && params.row.status !== 'disconnected') || blocked}
                  >
                    <QrCodeIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={blocked ? blockTooltip : "Logout"}>
                <IconButton
                    onClick={() => {
                      if (isInstanceBlocked(params.row)) {
                        showSnackbar('Instance is blocked for the first 3 minutes after creation', 'warning');
                        return;
                      }
                      logoutInstance(params.row.id);
                    }}
                    disabled={params.row.status === 'disconnected' || blocked}
                >
                  <LogoutIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title={blocked ? blockTooltip : "Restart"}>
                <IconButton
                    onClick={() => {
                      if (isInstanceBlocked(params.row)) {
                        showSnackbar('Instance is blocked for the first 3 minutes after creation', 'warning');
                        return;
                      }
                      restartInstance(params.row.id);
                    }}
                    disabled={blocked}
                >
                  <RestartIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title={blocked ? blockTooltip : "Delete"}>
                <IconButton
                    onClick={() => {
                      if (isInstanceBlocked(params.row)) {
                        showSnackbar('Instance is blocked for the first 3 minutes after creation', 'warning');
                        return;
                      }
                      setSelectedInstance(params.row);
                      setOpenDeleteDialog(true);
                    }}
                    color="error"
                    disabled={blocked}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
        );
      }
    }
  ];

  // Check if an instance is blocked (within 3 minutes of creation)
  const isInstanceBlocked = (instance) => {
    if (!instance || !instance.created_at) {
      return false;
    }

    const createdAt = new Date(instance.created_at);
    const now = new Date();
    const diffMs = now - createdAt;

    // Block period is 3 minutes (180000 ms)
    return diffMs < 180000;
  };


  // Get color based on status
  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'success.main';
      case 'disconnected':
        return 'error.main';
      case 'restarting':
        return 'warning.main';
      default:
        return 'text.primary';
    }
  };

  // Helper: filter by tags (comma-separated input supported)
  const filterInstancesByTags = (list, filter) => {
    const q = (filter || '').trim().toLowerCase();
    if (!q) return list;
    const tokens = q.split(',').map(s => s.trim()).filter(Boolean);
    return list.filter(inst => {
      const tagsStr = (inst.tags || '').toLowerCase();
      return tokens.every(tok => tagsStr.includes(tok));
    });
  };

  return (
      <DashboardLayout>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            WhatsApp Accounts
          </Typography>

          {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
            <TextField
              label="Filter by tags"
              size="small"
              value={tagsFilter}
              onChange={(e) => setTagsFilter(e.target.value)}
              placeholder="e.g. sales, vip"
            />
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenAddDialog(true)}
                sx={{
                  background: 'linear-gradient(45deg, #6a1b9a 30%, #9c4dcc 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #4a148c 30%, #7b1fa2 90%)',
                  }
                }}
            >
              Add Instance
            </Button>
          </Box>

          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <DataGrid
                rows={filterInstancesByTags(instances, tagsFilter)}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[5, 10, 25]}
                checkboxSelection={false}
                disableSelectionOnClick
                loading={loading}
                autoHeight
                sx={{
                  '& .MuiDataGrid-root': {
                    border: 'none',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'rgba(106, 27, 154, 0.1)',
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    backgroundColor: 'transparent',
                  }
                }}
            />
          </Paper>

          {/* Add Instance Dialog */}
          <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Add New WhatsApp Instance</DialogTitle>
            <DialogContent>
              <TextField
                  autoFocus
                  margin="dense"
                  label="Instance Alias"
                  fullWidth
                  variant="outlined"
                  value={newInstanceAlias}
                  onChange={(e) => setNewInstanceAlias(e.target.value)}
                  sx={{ mt: 2 }}
              />
              <FormControl sx={{ mt: 3 }}>
                <FormLabel id="instance-type-label">Instance Type</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="instance-type-label"
                  name="instance-type"
                  value={newInstanceType}
                  onChange={(e) => setNewInstanceType(e.target.value)}
                >
                  <FormControlLabel value="whatsapp" control={<Radio />} label="WhatsApp" />
                  <FormControlLabel value="max" control={<Radio />} label="Max" />
                </RadioGroup>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
              <Button
                  onClick={createInstance}
                  variant="contained"
                  disabled={!newInstanceAlias.trim()}
              >
                  Create
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
            <DialogTitle>Delete Instance</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete the instance "{selectedInstance?.alias}"?
                This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
              <Button onClick={deleteInstance} color="error" variant="contained">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* QR Code Dialog */}
          <Dialog open={openQrDialog} onClose={() => setOpenQrDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              {selectedInstance && String(selectedInstance.type || '').toLowerCase() === 'max' 
                ? `Connect MAX Instance - ${selectedInstance?.alias}`
                : `Connect WhatsApp Instance - ${selectedInstance?.alias}`}
              {qrCodeTimer > 0 && !qrCodeExpired && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    QR Code expires in: {qrCodeTimer}s
                  </Typography>
              )}
            </DialogTitle>
            <DialogContent sx={{ textAlign: 'center', py: 3 }}>
              {qrCode && !qrCodeExpired ? (
                  <Box>
                    <img
                        src={`data:image/png;base64,${qrCode}`}
                        alt="QR Code"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '400px',
                          border: '1px solid #ddd',
                          borderRadius: '8px'
                        }}
                    />
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                      {selectedInstance && String(selectedInstance.type || '').toLowerCase() === 'max'
                        ? 'Scan this QR code with MAX on your phone to connect the instance.'
                        : 'Scan this QR code with WhatsApp on your phone to connect the instance.'}
                    </Typography>
                  </Box>
              ) : qrCodeExpired ? (
                  <Box>
                    <Typography variant="h6" color="error" gutterBottom>
                      QR Code Expired
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      The QR code has expired. {selectedInstance && String(selectedInstance.type || '').toLowerCase() === 'max' 
                        ? 'A new QR code will be generated automatically.'
                        : 'Please generate a new one.'}
                    </Typography>
                    {(!selectedInstance || String(selectedInstance.type || '').toLowerCase() !== 'max') && (
                      <Button
                          variant="contained"
                          onClick={() => getInstanceQR(selectedInstance?.id)}
                          sx={{ mt: 2 }}
                      >
                        Generate New QR Code
                      </Button>
                    )}
                  </Box>
              ) : (
                  <CircularProgress />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenQrDialog(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Edit Message Limit Dialog */}
          <Dialog open={openEditLimitDialog} onClose={() => setOpenEditLimitDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Message Limit - {selectedInstance?.alias}</DialogTitle>
            <DialogContent>
              <TextField
                  autoFocus
                  margin="dense"
                  label="Daily Message Limit"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={newMessageLimit}
                  onChange={(e) => setNewMessageLimit(parseInt(e.target.value) || 0)}
                  sx={{ mt: 2 }}
                  inputProps={{ min: 1, max: 1000 }}
              />
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Set the maximum number of messages this instance can send per day (1-1000).
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenEditLimitDialog(false)}>Cancel</Button>
              <Button
                  onClick={updateMessageLimit}
                  variant="contained"
                  disabled={newMessageLimit < 1 || newMessageLimit > 1000}
              >
                Update
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Tags Dialog */}
          <Dialog open={openTagsDialog} onClose={() => setOpenTagsDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Tags - {selectedInstance?.alias}</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Tags (comma-separated)"
                fullWidth
                variant="outlined"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                sx={{ mt: 2 }}
                placeholder="e.g. vip, sales, test"
              />
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Enter tags separated by commas. Example: vip, sales, test
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenTagsDialog(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!selectedInstance) return;
                  try {
                    const payload = { tags: (tagsInput || '').split(',').map(t => t.trim()).filter(Boolean).join(',') };
                    const res = await api.put(`/instances/${selectedInstance.id}`, payload);
                    const updated = res.data?.instance || null;
                    setInstances(prev => prev.map(inst => inst.id === selectedInstance.id ? { ...inst, tags: updated?.tags ?? payload.tags } : inst));
                    setOpenTagsDialog(false);
                    setSelectedInstance(null);
                    showSnackbar('Tags updated successfully', 'success');
                  } catch (e) {
                    console.error('Error updating tags:', e);
                    showSnackbar(`Failed to update tags: ${e.response?.data?.message || 'Unknown error'}`, 'error');
                  }
                }}
                variant="contained"
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Rename Instance Dialog */}
          <Dialog open={openRenameDialog} onClose={() => setOpenRenameDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Rename Instance - {selectedInstance?.alias}</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="New Name"
                fullWidth
                variant="outlined"
                value={aliasInput}
                onChange={(e) => setAliasInput(e.target.value)}
                sx={{ mt: 2 }}
                placeholder="Enter instance name"
              />
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                The name is used only inside the app for your convenience.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenRenameDialog(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!selectedInstance) return;
                  const newName = (aliasInput || '').trim();
                  if (!newName) {
                    showSnackbar('Name cannot be empty', 'warning');
                    return;
                  }
                  try {
                    const res = await api.put(`/instances/${selectedInstance.id}`, { alias: newName });
                    const updated = res.data?.instance || null;
                    setInstances(prev => prev.map(inst => inst.id === selectedInstance.id ? { ...inst, alias: updated?.alias ?? newName } : inst));
                    setOpenRenameDialog(false);
                    setSelectedInstance(null);
                    showSnackbar('Name updated successfully', 'success');
                  } catch (e) {
                    console.error('Error renaming instance:', e);
                    showSnackbar(`Failed to update name: ${e.response?.data?.message || 'Unknown error'}`, 'error');
                  }
                }}
                variant="contained"
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Open Chat Dialog */}
          <Dialog open={openChatDialog} onClose={() => setOpenChatDialog(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Open WhatsApp Chat</DialogTitle>
            <DialogContent sx={{ textAlign: 'center', py: 3 }}>
              {chatPhone ? (
                <Box>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(`https://wa.me/${String(chatPhone).replace(/\\D/g, '')}`)}`}
                    alt="Chat QR Code"
                    style={{ maxWidth: '100%', border: '1px solid #ddd', borderRadius: 8 }}
                  />
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Scan to open chat, or click the link below:
                    </Typography>
                    <a
                      href={`https://wa.me/${String(chatPhone).replace(/\\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      https://wa.me/{String(chatPhone).replace(/\D/g, '')}
                    </a>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2">No phone number</Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenChatDialog(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for notifications */}
          <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </DashboardLayout>
  );
};

export default WAAccounts;